import androidx.compose.foundation.Canvas
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableFloatStateOf
import androidx.compose.runtime.mutableIntStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.withFrameNanos
import androidx.compose.ui.Modifier
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.graphics.BlendMode
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.StrokeCap
import androidx.compose.ui.graphics.drawscope.DrawScope
import androidx.compose.ui.graphics.drawscope.Stroke
import models.DomainGame
import models.Round
import models.RoundModifier
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.max
import kotlin.math.pow
import kotlin.math.sin
import kotlin.math.sqrt
import kotlin.random.Random

@Composable
fun FireworksGameEventEffects(
    game: DomainGame?,
    enabled: Boolean,
    modifier: Modifier = Modifier,
) {
    val snapshot = game?.round?.toFireworksSnapshot()
    var previousSnapshot by remember(game?.joinCode) { mutableStateOf<FireworksRoundSnapshot?>(null) }
    var nextEffectId by remember(game?.joinCode) { mutableIntStateOf(0) }
    var effects by remember(game?.joinCode) { mutableStateOf(emptyList<FireworksReaction>()) }
    var clockSeconds by remember { mutableFloatStateOf(0f) }

    LaunchedEffect(enabled) {
        var lastFrame = 0L
        while (enabled) {
            withFrameNanos { now ->
                if (lastFrame != 0L) {
                    clockSeconds += (now - lastFrame) / 1_000_000_000f
                    val active = effects.filter { clockSeconds - it.startSeconds <= it.durationSeconds }
                    if (active.size != effects.size) {
                        effects = active
                    }
                }
                lastFrame = now
            }
        }
    }

    LaunchedEffect(enabled, snapshot) {
        if (!enabled) {
            previousSnapshot = snapshot
            effects = emptyList()
            return@LaunchedEffect
        }

        val previous = previousSnapshot
        if (previous != null && snapshot != null) {
            classifyFireworksReaction(previous, snapshot)?.let { kind ->
                val seed = reactionSeed(snapshot, kind)
                effects = (effects + buildReaction(
                    id = nextEffectId++,
                    kind = kind,
                    startSeconds = clockSeconds,
                    seed = seed,
                    horizontalBias = rolledByBias(snapshot.rolledBy),
                    potSize = max(previous.currentPoints, snapshot.currentPoints),
                )).takeLast(MAX_ACTIVE_EFFECTS)
            }
        }
        previousSnapshot = snapshot
    }

    Canvas(modifier = modifier) {
        if (!enabled || size.width <= 0f || size.height <= 0f) return@Canvas
        effects.forEach { reaction ->
            drawReaction(reaction, clockSeconds - reaction.startSeconds)
        }
    }
}

private fun classifyFireworksReaction(
    previous: FireworksRoundSnapshot,
    current: FireworksRoundSnapshot,
): FireworksReactionKind? {
    if (previous.modifier != RoundModifier.FIREWORKS && current.modifier != RoundModifier.FIREWORKS) {
        return null
    }

    if (current.roundNum > previous.roundNum && current.hasVisibleDice) {
        return FireworksReactionKind.BUST
    }

    if (current.roundNum > previous.roundNum && current.modifier == RoundModifier.FIREWORKS) {
        return FireworksReactionKind.ROUND_START
    }

    if (!current.hasVisibleDice || current.sameVisibleRoll(previous)) {
        return null
    }

    val diceTotal = current.dieOne + current.dieTwo
    val safeRoll = previous.currentRoll <= RoundModifier.SAFE_ROLLS
    val lateRoll = previous.currentRoll > RoundModifier.SAFE_ROLLS
    return when {
        diceTotal == 7 && safeRoll -> FireworksReactionKind.SAFE_SEVEN
        current.dieOne == current.dieTwo && lateRoll -> FireworksReactionKind.LATE_DOUBLES
        else -> null
    }
}

private fun buildReaction(
    id: Int,
    kind: FireworksReactionKind,
    startSeconds: Float,
    seed: Int,
    horizontalBias: Float,
    potSize: Int,
): FireworksReaction {
    val random = Random(seed)
    val center = Offset(
        x = (0.5f + horizontalBias * 0.12f).coerceIn(0.16f, 0.84f),
        y = 0.42f,
    )
    val potBoost = (potSize / 120f).coerceIn(0f, 0.8f)
    val builder = FireworksReactionBuilder(random)

    when (kind) {
        FireworksReactionKind.SAFE_SEVEN -> {
            builder.flash(center, GOLD, startDelay = 0f, life = 0.32f, radius = 0.2f)
            builder.ring(center, GOLD, startDelay = 0.04f, life = 0.8f, radius = 0.38f, stroke = 4.2f)
            builder.chrysanthemum(
                center = center,
                colors = listOf(WHITE, GOLD, BLUE),
                count = 88,
                speed = 0.34f + potBoost * 0.08f,
                life = 1.55f,
                startDelay = 0.02f,
                gravity = 0.09f,
                sparkle = true,
            )
            builder.palmComets(
                origin = Offset(center.x, 0.94f),
                target = center,
                colors = listOf(GOLD, WHITE),
                count = 14,
                startDelay = 0f,
                life = 1.05f,
            )
            builder.crackle(center, listOf(WHITE, GOLD), count = 28, startDelay = 0.46f)
        }

        FireworksReactionKind.LATE_DOUBLES -> {
            val left = Offset(0.32f, 0.37f)
            val right = Offset(0.68f, 0.37f)
            builder.flash(Offset(0.5f, 0.45f), WHITE, startDelay = 0f, life = 0.24f, radius = 0.3f)
            builder.ring(Offset(0.5f, 0.47f), GOLD, startDelay = 0.02f, life = 0.92f, radius = 0.58f, stroke = 5f)
            builder.chrysanthemum(left, listOf(RED, WHITE, GOLD), count = 76, speed = 0.34f, life = 1.45f, startDelay = 0f, gravity = 0.11f)
            builder.chrysanthemum(right, listOf(BLUE, WHITE, GOLD), count = 76, speed = 0.34f, life = 1.45f, startDelay = 0.08f, gravity = 0.11f)
            builder.ringBurst(Offset(0.5f, 0.43f), listOf(RED, WHITE, BLUE), count = 54, startDelay = 0.26f)
            builder.willow(Offset(0.5f, 0.28f), listOf(GOLD, WHITE), count = 54, startDelay = 0.5f)
            builder.crackle(left, listOf(WHITE, RED), count = 24, startDelay = 0.54f)
            builder.crackle(right, listOf(WHITE, BLUE), count = 24, startDelay = 0.62f)
        }

        FireworksReactionKind.BUST -> {
            val blast = Offset(0.5f, 0.5f)
            builder.flash(blast, RED, startDelay = 0f, life = 0.22f, radius = 0.44f)
            builder.ring(blast, RED, startDelay = 0f, life = 0.86f, radius = 0.72f, stroke = 6.5f)
            builder.ring(blast, WHITE, startDelay = 0.05f, life = 0.68f, radius = 0.56f, stroke = 2.2f)
            builder.burstDebris(blast, listOf(RED, GOLD, WHITE), count = 96, startDelay = 0.02f)
            builder.emberRain(listOf(RED, GOLD), count = 64, startDelay = 0.1f)
        }

        FireworksReactionKind.ROUND_START -> {
            repeat(5) { index ->
                val target = Offset(
                    x = 0.18f + index * 0.16f + random.nextFloat() * 0.035f,
                    y = 0.16f + random.nextFloat() * 0.18f,
                )
                val origin = Offset(
                    x = (target.x + (index - 2) * 0.06f).coerceIn(0.06f, 0.94f),
                    y = 1.03f,
                )
                builder.launchTrail(origin, target, listOf(RED, WHITE, BLUE)[index % 3], startDelay = index * 0.05f)
                builder.chrysanthemum(
                    center = target,
                    colors = listOf(RED, WHITE, BLUE, GOLD),
                    count = 32,
                    speed = 0.18f,
                    life = 1.2f,
                    startDelay = 0.46f + index * 0.05f,
                    gravity = 0.06f,
                    sparkle = index % 2 == 0,
                )
            }
            builder.ring(Offset(0.5f, 0.36f), BLUE, startDelay = 0.56f, life = 1.2f, radius = 0.46f, stroke = 3.2f)
        }
    }

    return builder.build(id, kind, startSeconds)
}

private class FireworksReactionBuilder(
    private val random: Random,
) {
    private val particles = mutableListOf<FireworksParticle>()
    private val rings = mutableListOf<FireworksRing>()
    private val flashes = mutableListOf<FireworksFlash>()

    fun chrysanthemum(
        center: Offset,
        colors: List<Color>,
        count: Int,
        speed: Float,
        life: Float,
        startDelay: Float,
        gravity: Float,
        sparkle: Boolean = false,
    ) {
        repeat(count) { index ->
            val angle = index / count.toFloat() * TWO_PI + random.nextFloat() * 0.09f
            val variance = 0.76f + random.nextFloat() * 0.34f
            val velocity = Offset(cos(angle) * speed * variance, sin(angle) * speed * variance)
            particles += FireworksParticle(
                origin = center,
                velocity = velocity,
                color = colors[index % colors.size],
                startDelay = startDelay + random.nextFloat() * 0.05f,
                life = life * (0.82f + random.nextFloat() * 0.3f),
                radius = 2.4f + random.nextFloat() * 2.8f,
                tailSeconds = 0.18f + random.nextFloat() * 0.12f,
                drag = 0.48f,
                gravity = gravity,
                flicker = if (sparkle) 0.62f else 0.28f,
                glow = 1.2f,
                shape = if (sparkle && index % 7 == 0) ParticleShape.STAR else ParticleShape.SPARK,
            )
        }
    }

    fun ringBurst(center: Offset, colors: List<Color>, count: Int, startDelay: Float) {
        repeat(count) { index ->
            val angle = index / count.toFloat() * TWO_PI
            particles += FireworksParticle(
                origin = center,
                velocity = Offset(cos(angle) * 0.28f, sin(angle) * 0.28f),
                color = colors[index % colors.size],
                startDelay = startDelay,
                life = 1.15f + random.nextFloat() * 0.22f,
                radius = 2.1f + random.nextFloat() * 1.8f,
                tailSeconds = 0.16f,
                drag = 0.35f,
                gravity = 0.04f,
                flicker = 0.36f,
                glow = 1f,
                shape = ParticleShape.SPARK,
            )
        }
    }

    fun willow(center: Offset, colors: List<Color>, count: Int, startDelay: Float) {
        repeat(count) { index ->
            val angle = (-PI.toFloat() * 0.95f) + index / count.toFloat() * PI.toFloat() * 0.9f
            val velocity = Offset(cos(angle) * (0.2f + random.nextFloat() * 0.07f), sin(angle) * (0.28f + random.nextFloat() * 0.08f))
            particles += FireworksParticle(
                origin = center,
                velocity = velocity,
                color = colors[index % colors.size],
                startDelay = startDelay + random.nextFloat() * 0.08f,
                life = 1.65f + random.nextFloat() * 0.3f,
                radius = 2f + random.nextFloat() * 2f,
                tailSeconds = 0.34f,
                drag = 0.42f,
                gravity = 0.24f,
                flicker = 0.2f,
                glow = 0.95f,
                shape = ParticleShape.SPARK,
            )
        }
    }

    fun palmComets(origin: Offset, target: Offset, colors: List<Color>, count: Int, startDelay: Float, life: Float) {
        repeat(count) { index ->
            val t = index / (count - 1).coerceAtLeast(1).toFloat()
            val launchTarget = Offset(
                x = target.x + (t - 0.5f) * 0.5f,
                y = target.y + random.nextFloat() * 0.14f,
            )
            val velocity = Offset(
                x = (launchTarget.x - origin.x) / life,
                y = (launchTarget.y - origin.y) / life,
            )
            particles += FireworksParticle(
                origin = origin,
                velocity = velocity,
                color = colors[index % colors.size],
                startDelay = startDelay + random.nextFloat() * 0.12f,
                life = life,
                radius = 2.3f + random.nextFloat() * 2f,
                tailSeconds = 0.28f,
                drag = 0.02f,
                gravity = 0.02f,
                flicker = 0.22f,
                glow = 1.05f,
                shape = ParticleShape.COMET,
            )
        }
    }

    fun launchTrail(origin: Offset, target: Offset, color: Color, startDelay: Float) {
        val life = 0.62f
        repeat(18) { index ->
            val delay = startDelay + index * 0.018f
            particles += FireworksParticle(
                origin = origin,
                velocity = Offset((target.x - origin.x) / life, (target.y - origin.y) / life),
                color = if (index % 4 == 0) WHITE else color,
                startDelay = delay,
                life = life,
                radius = 1.8f + random.nextFloat() * 2.2f,
                tailSeconds = 0.22f,
                drag = 0.03f,
                gravity = 0.01f,
                flicker = 0.38f,
                glow = 1.2f,
                shape = ParticleShape.COMET,
            )
        }
    }

    fun crackle(center: Offset, colors: List<Color>, count: Int, startDelay: Float) {
        repeat(count) {
            val angle = random.nextFloat() * TWO_PI
            val distance = 0.1f + random.nextFloat() * 0.22f
            particles += FireworksParticle(
                origin = center,
                velocity = Offset(cos(angle) * distance, sin(angle) * distance),
                color = colors[it % colors.size],
                startDelay = startDelay + random.nextFloat() * 0.36f,
                life = 0.36f + random.nextFloat() * 0.24f,
                radius = 1.4f + random.nextFloat() * 2.6f,
                tailSeconds = 0.055f,
                drag = 0.2f,
                gravity = 0.03f,
                flicker = 0.95f,
                glow = 1.45f,
                shape = ParticleShape.STAR,
            )
        }
    }

    fun burstDebris(center: Offset, colors: List<Color>, count: Int, startDelay: Float) {
        repeat(count) { index ->
            val angle = random.nextFloat() * TWO_PI
            val speed = 0.18f + random.nextFloat() * 0.48f
            particles += FireworksParticle(
                origin = center,
                velocity = Offset(cos(angle) * speed, sin(angle) * speed),
                color = colors[index % colors.size],
                startDelay = startDelay + random.nextFloat() * 0.1f,
                life = 0.9f + random.nextFloat() * 0.5f,
                radius = 2f + random.nextFloat() * 3.8f,
                tailSeconds = 0.16f,
                drag = 0.55f,
                gravity = 0.22f,
                flicker = 0.56f,
                glow = 1.15f,
                shape = if (index % 8 == 0) ParticleShape.STAR else ParticleShape.SPARK,
            )
        }
    }

    fun emberRain(colors: List<Color>, count: Int, startDelay: Float) {
        repeat(count) { index ->
            val x = random.nextFloat()
            particles += FireworksParticle(
                origin = Offset(x, -0.16f - random.nextFloat() * 0.18f),
                velocity = Offset((random.nextFloat() - 0.5f) * 0.12f, 0.34f + random.nextFloat() * 0.32f),
                color = colors[index % colors.size],
                startDelay = startDelay + random.nextFloat() * 0.42f,
                life = 1.1f + random.nextFloat() * 0.6f,
                radius = 2f + random.nextFloat() * 3.2f,
                tailSeconds = 0.2f,
                drag = 0.18f,
                gravity = 0.12f,
                flicker = 0.48f,
                glow = 0.9f,
                shape = ParticleShape.EMBER,
            )
        }
    }

    fun ring(center: Offset, color: Color, startDelay: Float, life: Float, radius: Float, stroke: Float) {
        rings += FireworksRing(center, color, startDelay, life, radius, stroke)
    }

    fun flash(center: Offset, color: Color, startDelay: Float, life: Float, radius: Float) {
        flashes += FireworksFlash(center, color, startDelay, life, radius)
    }

    fun build(id: Int, kind: FireworksReactionKind, startSeconds: Float): FireworksReaction {
        val duration = max(
            rings.maxOfOrNull { it.startDelay + it.life } ?: 0f,
            max(
                flashes.maxOfOrNull { it.startDelay + it.life } ?: 0f,
                particles.maxOfOrNull { it.startDelay + it.life } ?: kind.minimumDurationSeconds,
            ),
        ) + 0.15f
        return FireworksReaction(id, kind, startSeconds, duration, particles, rings, flashes)
    }
}

private fun DrawScope.drawReaction(reaction: FireworksReaction, ageSeconds: Float) {
    reaction.flashes.forEach { drawFlash(it, ageSeconds) }
    reaction.rings.forEach { drawRing(it, ageSeconds) }
    reaction.particles.forEach { drawParticle(it, ageSeconds) }
}

private fun DrawScope.drawParticle(particle: FireworksParticle, reactionAge: Float) {
    val age = reactionAge - particle.startDelay
    if (age < 0f || age > particle.life) return

    val progress = (age / particle.life).coerceIn(0f, 1f)
    val fade = (1f - progress).pow(1.35f)
    val flicker = 1f - particle.flicker * 0.42f * flickerWave(age, particle.velocity)
    val alpha = (0.12f + fade * 0.76f) * flicker
    if (alpha <= 0.01f) return

    val center = particle.positionAt(age, size.width, size.height, size.minDimension)
    val tailAge = max(0f, age - particle.tailSeconds)
    val tail = particle.positionAt(tailAge, size.width, size.height, size.minDimension)
    val radius = particle.radius * (0.65f + fade * 0.7f)

    drawLine(
        color = particle.color.copy(alpha = alpha * 0.26f),
        start = tail,
        end = center,
        strokeWidth = radius * 0.72f,
        cap = StrokeCap.Round,
        blendMode = BlendMode.Plus,
    )
    if (particle.glow > 0f) {
        drawCircle(
            color = particle.color.copy(alpha = alpha * 0.06f * particle.glow),
            radius = radius * (4.2f + particle.glow),
            center = center,
            blendMode = BlendMode.Plus,
        )
    }
    drawCircle(
        color = particle.color.copy(alpha = alpha * 0.42f),
        radius = radius,
        center = center,
        blendMode = BlendMode.Plus,
    )
    if (particle.shape == ParticleShape.STAR && progress < 0.84f) {
        val arm = radius * (2.3f + particle.glow)
        drawLine(
            color = WHITE.copy(alpha = alpha * 0.18f),
            start = Offset(center.x - arm, center.y),
            end = Offset(center.x + arm, center.y),
            strokeWidth = 1.1f,
            cap = StrokeCap.Round,
            blendMode = BlendMode.Plus,
        )
        drawLine(
            color = WHITE.copy(alpha = alpha * 0.18f),
            start = Offset(center.x, center.y - arm),
            end = Offset(center.x, center.y + arm),
            strokeWidth = 1.1f,
            cap = StrokeCap.Round,
            blendMode = BlendMode.Plus,
        )
    }
}

private fun DrawScope.drawRing(ring: FireworksRing, reactionAge: Float) {
    val age = reactionAge - ring.startDelay
    if (age < 0f || age > ring.life) return

    val progress = age / ring.life
    val fade = (1f - progress).pow(1.25f)
    val center = ring.center.toPx(size.width, size.height)
    val radius = size.minDimension * ring.radius * easeOutCubic(progress)
    drawCircle(
        color = ring.color.copy(alpha = fade * 0.24f),
        radius = radius,
        center = center,
        style = Stroke(width = ring.strokeWidth * fade + 1.1f),
        blendMode = BlendMode.Plus,
    )
    drawCircle(
        color = ring.color.copy(alpha = fade * 0.035f),
        radius = radius * 0.72f,
        center = center,
        blendMode = BlendMode.Plus,
    )
}

private fun DrawScope.drawFlash(flash: FireworksFlash, reactionAge: Float) {
    val age = reactionAge - flash.startDelay
    if (age < 0f || age > flash.life) return

    val progress = age / flash.life
    val fade = (1f - progress).pow(1.8f)
    val center = flash.center.toPx(size.width, size.height)
    drawCircle(
        color = flash.color.copy(alpha = 0.12f * fade),
        radius = size.minDimension * flash.radius * (0.42f + progress),
        center = center,
        blendMode = BlendMode.Plus,
    )
    drawCircle(
        color = WHITE.copy(alpha = 0.24f * fade),
        radius = size.minDimension * flash.radius * 0.08f,
        center = center,
        blendMode = BlendMode.Plus,
    )
}

private fun FireworksParticle.positionAt(age: Float, width: Float, height: Float, scale: Float): Offset {
    val dampedAge = age / (1f + drag * age)
    val originPx = origin.toPx(width, height)
    return Offset(
        x = originPx.x + velocity.x * scale * dampedAge,
        y = originPx.y + velocity.y * scale * dampedAge + gravity * scale * age * age,
    )
}

private fun Offset.toPx(width: Float, height: Float): Offset =
    Offset(x * width, y * height)

private fun flickerWave(age: Float, velocity: Offset): Float {
    val frequency = 22f + (abs(velocity.x) + abs(velocity.y)) * 14f
    return ((sin(age * frequency) + sin(age * frequency * 1.73f + 1.2f)) * 0.5f)
        .coerceIn(0f, 1f)
}

private fun abs(value: Float): Float = if (value < 0f) -value else value

private fun Round.toFireworksSnapshot(): FireworksRoundSnapshot =
    FireworksRoundSnapshot(
        roundNum = roundNum,
        currentRoll = currentRoll,
        currentPoints = currentPoints,
        dieOne = dieOne,
        dieTwo = dieTwo,
        modifier = modifier,
        rolledBy = rolledBy,
    )

private fun FireworksRoundSnapshot.sameVisibleRoll(other: FireworksRoundSnapshot): Boolean =
    roundNum == other.roundNum &&
        currentRoll == other.currentRoll &&
        dieOne == other.dieOne &&
        dieTwo == other.dieTwo &&
        rolledBy == other.rolledBy

private fun reactionSeed(snapshot: FireworksRoundSnapshot, kind: FireworksReactionKind): Int =
    snapshot.roundNum * 10_007 +
        snapshot.currentRoll * 1_009 +
        snapshot.currentPoints * 31 +
        snapshot.dieOne * 97 +
        snapshot.dieTwo * 193 +
        snapshot.rolledBy.hashCode() +
        kind.ordinal * 397

private fun rolledByBias(rolledBy: String): Float =
    if (rolledBy.isBlank()) {
        0f
    } else {
        ((rolledBy.hashCode() and 0xFF) / 255f - 0.5f) * 2f
    }

private fun easeOutCubic(value: Float): Float {
    val inverse = 1f - value.coerceIn(0f, 1f)
    return 1f - inverse * inverse * inverse
}

private fun Offset.length(): Float = sqrt(x * x + y * y)

private data class FireworksRoundSnapshot(
    val roundNum: Int,
    val currentRoll: Int,
    val currentPoints: Int,
    val dieOne: Int,
    val dieTwo: Int,
    val modifier: RoundModifier,
    val rolledBy: String,
) {
    val hasVisibleDice: Boolean
        get() = dieOne != 0 || dieTwo != 0
}

private data class FireworksReaction(
    val id: Int,
    val kind: FireworksReactionKind,
    val startSeconds: Float,
    val durationSeconds: Float,
    val particles: List<FireworksParticle>,
    val rings: List<FireworksRing>,
    val flashes: List<FireworksFlash>,
)

private data class FireworksParticle(
    val origin: Offset,
    val velocity: Offset,
    val color: Color,
    val startDelay: Float,
    val life: Float,
    val radius: Float,
    val tailSeconds: Float,
    val drag: Float,
    val gravity: Float,
    val flicker: Float,
    val glow: Float,
    val shape: ParticleShape,
)

private data class FireworksRing(
    val center: Offset,
    val color: Color,
    val startDelay: Float,
    val life: Float,
    val radius: Float,
    val strokeWidth: Float,
)

private data class FireworksFlash(
    val center: Offset,
    val color: Color,
    val startDelay: Float,
    val life: Float,
    val radius: Float,
)

private enum class ParticleShape {
    SPARK,
    STAR,
    COMET,
    EMBER,
}

private enum class FireworksReactionKind(val minimumDurationSeconds: Float) {
    SAFE_SEVEN(1.7f),
    LATE_DOUBLES(2.2f),
    BUST(1.7f),
    ROUND_START(2.5f),
}

private val WHITE = Color(0xFFFFFFFF)
private val RED = Color(0xFFE53E3E)
private val BLUE = Color(0xFF2563EB)
private val GOLD = Color(0xFFFFD166)

private const val MAX_ACTIVE_EFFECTS = 6
private const val FRAME_NANOS = 16_000_000L
private const val TWO_PI = (PI * 2).toFloat()
